/**
 * Изображение переговорной.
 *
 * Фотографий у комнат нет и, скорее всего, не будет: загрузки файлов в системе
 * нет, есть только поле со ссылкой, а хостить снимки негде. До этого компонента
 * на всех экранах стояла серая плашка «Фотографии нет» — на демонстрации она
 * бросалась в глаза у каждой из трёх комнат.
 *
 * Поэтому изображение рисуется, а не хранится. Это не заглушка «пока не подвезли
 * фото», а осознанный выбор:
 *
 *   * ничего не нужно хостить, и в поставку не едет ни одного мегабайта картинок;
 *   * новая переговорная получает своё изображение сразу, без участия человека;
 *   * рисунок не зависит от сети — планшет у двери работает и при её обрыве;
 *   * он темнеет вместе с интерфейсом, чего фотография не умеет.
 *
 * Если администратор всё-таки пропишет ссылку на снимок, показывается снимок:
 * настоящая фотография комнаты полезнее любого рисунка.
 *
 * Композиция выводится из вместимости — большой стол, круглый или на двоих, —
 * а оттенок и мелкие детали из имени комнаты. Поэтому две переговорные рядом
 * выглядят по-разному, а одна и та же всегда одинаково: человек запоминает
 * картинку и узнаёт комнату по ней.
 */
import { useId } from 'react'
import { cn } from '../lib/cn'

interface Props {
  name: string
  capacity: number
  /** Ссылка на настоящую фотографию. Если задана — показываем её. */
  photoUrl?: string
  className?: string
}

/**
 * Устойчивый numeric-отпечаток строки.
 *
 * Нужен, чтобы одна и та же комната всегда получала одно и то же изображение.
 * Брать id нельзя: в разных списках комната приходит то с объектом, то плоским
 * полем, а имя есть везде.
 */
function fingerprint(value: string): number {
  let hash = 0
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) | 0
  }
  return Math.abs(hash)
}

/**
 * Оттенки комнат.
 *
 * Цвета берутся НЕ из этого списка напрямую: заливки строятся на токенах темы
 * (--c-surface-2, --c-surface-3, --c-line), а отсюда приходит только оттенок,
 * который кладётся поверх с малой прозрачностью. Иначе рисунок оставался бы
 * светлым в тёмной теме и светил бы белым пятном посреди тёмного экрана —
 * ровно это и было в первой версии.
 *
 * Фирменный синий #0F2EFD слишком насыщен, чтобы заливать им плоскости: рядом
 * с ним не читается ни текст, ни соседние элементы. Поэтому он появляется одной
 * деталью — экраном на стене, — и берётся тоже из темы: в тёмной он светлее.
 */
const HUES = ['#4C6FE8', '#3E9C86', '#C08A4A', '#8B6BC4']

export function RoomImage({ name, capacity, photoUrl, className }: Props) {
  // Идентификаторы градиентов обязаны быть уникальными на странице: на экране
  // дня рисунков несколько, и одинаковый id заставил бы их делить одну заливку.
  const uid = useId().replace(/:/g, '')

  const seed = fingerprint(name)
  const hue = HUES[seed % HUES.length]!
  // Три композиции по вместимости: они и различают комнаты по назначению.
  const layout = capacity >= 14 ? 'boardroom' : capacity >= 6 ? 'round' : 'pair'

  /*
    Размер задаёт рамка, а не сам рисунок, и это здесь принципиально.

    cn() просто склеивает классы, а Tailwind разрешает спор одинаковых по весу
    правил порядком в собранном CSS: .h-full и .w-full стоят там ПОСЛЕ .h-14
    и .w-20, потому что в шкале Tailwind дробные и «full» идут за числовыми.
    Значит, стоило компоненту оставить h-full/w-full на том же элементе, куда
    приходит className, — и любой размер снаружи молча проигрывал. На узком
    экране рисунок раздувался на всю ширину карточки (320:180 при w-full — это
    сотни пикселей высоты), выдавливал текст и превращался в синее пятно.

    Поэтому h-full/w-full живут внутри, на элементе, до которого className
    не дотягивается: спорить стало нечему.
  */
  return (
    <span className={cn('block overflow-hidden', className)}>
      {photoUrl ? (
        <img src={photoUrl} alt={name} className="block h-full w-full object-cover" />
      ) : (
        <RoomDrawing uid={uid} name={name} capacity={capacity} hue={hue} layout={layout} />
      )}
    </span>
  )
}

type Layout = 'boardroom' | 'round' | 'pair'

function RoomDrawing({
  uid,
  name,
  capacity,
  hue,
  layout,
}: {
  uid: string
  name: string
  capacity: number
  hue: string
  layout: Layout
}) {
  return (
    <svg
      viewBox="0 0 320 180"
      className="block h-full w-full"
      role="img"
      aria-label={`Переговорная «${name}», ${capacity} мест`}
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <linearGradient id={`wall-${uid}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgb(var(--c-surface-2))" />
          <stop offset="100%" stopColor="rgb(var(--c-surface-3))" />
        </linearGradient>
      </defs>

      <rect width="320" height="180" fill={`url(#wall-${uid})`} />
      {/* Оттенок комнаты: тонкой плёнкой поверх темы, а не вместо неё. */}
      <rect width="320" height="180" fill={hue} opacity="0.10" />
      {/* Пол: горизонт на двух третях высоты — комната читается как объём. */}
      <rect y="118" width="320" height="62" fill={hue} opacity="0.14" />

      {/* Экран на стене — единственная деталь фирменного синего. */}
      <rect
        x={layout === 'pair' ? 208 : 24}
        y="30"
        width="72"
        height="44"
        rx="4"
        fill="rgb(var(--c-accent))"
        opacity="0.9"
      />

      {/* Окно: светлый прямоугольник с переплётом. */}
      <g opacity="0.75">
        <rect
          x={layout === 'pair' ? 34 : 214}
          y="26"
          width="76"
          height="52"
          rx="3"
          fill="rgb(var(--c-surface))"
        />
        <line
          x1={layout === 'pair' ? 72 : 252}
          y1="26"
          x2={layout === 'pair' ? 72 : 252}
          y2="78"
          stroke="rgb(var(--c-line))"
          strokeWidth="2"
        />
      </g>

      {layout === 'boardroom' && <Boardroom hue={hue} />}
      {layout === 'round' && <RoundTable hue={hue} />}
      {layout === 'pair' && <PairTable hue={hue} />}
    </svg>
  )
}

/** Большая переговорная: длинный стол и ряды кресел по обе стороны. */
function Boardroom({ hue }: { hue: string }) {
  const seats = [64, 104, 144, 184, 224]
  return (
    <g>
      {seats.map((x) => (
        <rect key={`top-${x}`} x={x} y="96" width="26" height="14" rx="6" fill={hue} opacity="0.5" />
      ))}
      <rect x="52" y="112" width="216" height="30" rx="8" fill={hue} opacity="0.5" />
      <rect x="52" y="112" width="216" height="10" rx="5" fill="rgb(var(--c-surface))" opacity="0.35" />
      {seats.map((x) => (
        <rect
          key={`bottom-${x}`}
          x={x}
          y="146"
          width="26"
          height="14"
          rx="6"
          fill={hue} opacity="0.5"
        />
      ))}
    </g>
  )
}

/** Средняя переговорная: круглый стол, кресла вокруг. */
function RoundTable({ hue }: { hue: string }) {
  const around = [
    [110, 100],
    [160, 94],
    [210, 100],
    [104, 142],
    [160, 150],
    [216, 142],
  ]
  return (
    <g>
      {around.map(([x, y]) => (
        <circle key={`${x}-${y}`} cx={x} cy={y} r="9" fill={hue} opacity="0.5" />
      ))}
      <ellipse cx="160" cy="124" rx="58" ry="22" fill={hue} opacity="0.5" />
      <ellipse cx="160" cy="120" rx="58" ry="22" fill="rgb(var(--c-surface))" opacity="0.3" />
    </g>
  )
}

/** Малая переговорная: столик на двоих. */
function PairTable({ hue }: { hue: string }) {
  return (
    <g>
      <circle cx="118" cy="126" r="11" fill={hue} opacity="0.5" />
      <circle cx="196" cy="126" r="11" fill={hue} opacity="0.5" />
      <ellipse cx="157" cy="128" rx="30" ry="16" fill={hue} opacity="0.5" />
      <ellipse cx="157" cy="124" rx="30" ry="16" fill="rgb(var(--c-surface))" opacity="0.3" />
    </g>
  )
}
